import React, { useEffect, useState } from 'react';
import { 
  CheckCircle, 
  Play, 
  Award,
  BookOpen,
  Loader2,
  Trophy,
  Rocket,
  MapPin,
  Sparkles,
  History
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { learningPlansService, LikedCourse } from '../../services/firebase/learningPlans';
import { courseService } from '../../services/firebase/courses';
import { db } from '../../services/firebase/config';
import { doc, getDoc } from 'firebase/firestore';
import { Card } from '../common/Card';
import { Button } from '../common/Button';

// Extended interface to include AI recommended courses not yet "liked"
interface ExtendedCourse extends LikedCourse {
  isAiRecommended?: boolean;
  isCurrentPlan?: boolean;
}

interface JourneyData {
  completedCourses: ExtendedCourse[];
  inProgressCourses: ExtendedCourse[];
  currentPlanToStartCourses: ExtendedCourse[];
  previousPlansToStartCourses: ExtendedCourse[];
}

export const LearningJourneyTimeline: React.FC = () => {
  const { user } = useAuth();
  const [journeyData, setJourneyData] = useState<JourneyData | null>(null);
  const [loading, setLoading] = useState(true);
  const [aiQuotient, setAiQuotient] = useState<number>(0);

  useEffect(() => {
    const fetchJourney = async () => {
      if (!user) return;
      
      try {
        setLoading(true);
        // 1. Get all user learning plans
        const userPlans = await learningPlansService.getAllUserLearningPlans();
        
        // Sort by date descending (newest first)
        const sortedPlans = userPlans.sort((a, b) => b.createdAt.toMillis() - a.createdAt.toMillis());

        // Determine the current learning plan ID from the user document
        // This is the authoritative source of truth for what is "current"
        const currentLearningPlanId = user?.currentLearningPlanId;

        const uniqueCoursesMap = new Map<string, ExtendedCourse>();

        // 2. Fetch the current learning plan's recommended courses to determine what's "current"
        let currentPlanCourseIds = new Set<string>();
        
        if (currentLearningPlanId) {
          try {
            const planDocRef = doc(db, 'learningPlans', currentLearningPlanId);
            const planDoc = await getDoc(planDocRef);
            if (planDoc.exists()) {
              const data = planDoc.data();
              const recommended = (data.recommendedCourses as any[]) || [];
              recommended.forEach(c => {
                if (c.courseId && c.status === 'active') {
                  currentPlanCourseIds.add(c.courseId);
                }
              });
            }
          } catch (e) {
            console.error("Error fetching current learning plan:", e);
          }
        }

        // 3. Aggregate all courses from all plans
        // Mark courses as "current" if they're in the current learning plan's recommended courses
        for (const userPlan of sortedPlans) {
          const planCourses: ExtendedCourse[] = userPlan.likedCourses.map(c => ({
            ...c,
            isCurrentPlan: currentPlanCourseIds.has(c.courseId)
          }));

          // AI recommended courses are intentionally NOT added automatically here to avoid clutter.
          // Users should see only courses they have explicitly liked/added to their plan.
          /* 
          // Fetch AI recommended courses if planId exists
          if (userPlan.planId) {
            try {
              const aiPlanDoc = await getDoc(doc(db, 'learningPlans', userPlan.planId));
              if (aiPlanDoc.exists()) {
                const aiData = aiPlanDoc.data();
                const recommended = (aiData.recommendedCourses || []) as any[];
                
                // Add recommended courses
                const existingIds = new Set(planCourses.map(c => c.courseId));
                recommended.forEach(rc => {
                  if (!existingIds.has(rc.courseId) && rc.status === 'active') {
                     planCourses.push({
                        courseId: rc.courseId,
                        courseTitle: rc.courseTitle,
                        addedAt: userPlan.createdAt, // Use plan creation date as added date
                        progress: 0,
                        timeSpent: 0,
                        priority: 3,
                        source: 'recommendation',
                        status: 'active',
                        isAiRecommended: true,
                        isCurrentPlan
                     });
                  }
                });
              }
            } catch (e) {
              console.error(`Failed to fetch AI plan ${userPlan.planId}`, e);
            }
          }
          */

          planCourses.forEach(c => {
            if (!uniqueCoursesMap.has(c.courseId)) {
              uniqueCoursesMap.set(c.courseId, c);
            }
          });
        }

        const uniqueCourses = Array.from(uniqueCoursesMap.values());

        // 3. Fetch up-to-date progress
        const progressPromises = uniqueCourses.map(async (course) => {
           try {
              const progress = await courseService.getUserProgress(course.courseId);
              if (progress) {
                 // Preserve startedAt from course if it exists, otherwise use progress data
                 // This ensures we don't lose startedAt when progress data doesn't have it
                 const startedAt = course.startedAt || progress.startedAt || progress.lastAccessedAt || progress.enrolledAt;
                 
                 return {
                    ...course,
                    progress: progress.completionPercentage || 0,
                    status: (progress.completionPercentage >= 100 || progress.completedAt) ? 'completed' : 'active',
                    completedAt: progress.completedAt,
                    lastAccessedAt: progress.lastAccessedAt,
                    startedAt
                 } as ExtendedCourse;
              }
           } catch (e) {
              // ignore
           }
           return course;
        });
        
        const consolidatedCourses = (await Promise.all(progressPromises)) as ExtendedCourse[];

        // 4. Group courses
        const completed: ExtendedCourse[] = [];
        const inProgress: ExtendedCourse[] = [];
        const currentToStart: ExtendedCourse[] = [];
        const previousToStart: ExtendedCourse[] = [];

        consolidatedCourses.forEach(course => {
          // A course is completed if marked as completed or has 100% progress
          if (course.status === 'completed' || course.progress >= 100) {
            completed.push(course);
          } 
          // A course is "in progress" if the user has clicked "Start" (startedAt exists)
          // This includes courses at 0% progress - any course that has been started is in progress
          else if (course.startedAt || course.progress > 0) {
            inProgress.push(course);
          } 
          // Otherwise, categorize as "to start" based on whether it's in the current plan
          else {
            if (course.isCurrentPlan) {
              currentToStart.push(course);
            } else {
              previousToStart.push(course);
            }
          }
        });

        // 5. Sort groups
        completed.sort((a, b) => (b.completedAt?.toMillis() || 0) - (a.completedAt?.toMillis() || 0));
        inProgress.sort((a, b) => (b.lastAccessedAt?.toMillis() || 0) - (a.lastAccessedAt?.toMillis() || 0));
        // For to-start, sort by addedAt
        currentToStart.sort((a, b) => (b.addedAt?.toMillis() || 0) - (a.addedAt?.toMillis() || 0));
        previousToStart.sort((a, b) => (b.addedAt?.toMillis() || 0) - (a.addedAt?.toMillis() || 0));

        setJourneyData({
          completedCourses: completed,
          inProgressCourses: inProgress,
          currentPlanToStartCourses: currentToStart,
          previousPlansToStartCourses: previousToStart
        });

        // Calculate AI Quotient (average category coverage)
        await calculateAIQuotient([...completed, ...inProgress, ...currentToStart, ...previousToStart]);

      } catch (error) {
        console.error("Error fetching learning journey:", error);
      } finally {
        setLoading(false);
      }
    };

  // Function to calculate AI Quotient
  const calculateAIQuotient = async (courses: ExtendedCourse[]) => {
    if (courses.length === 0) {
      setAiQuotient(0);
      return;
    }

    try {
      const categoryMap = new Map<string, { total: number; completed: number }>();

      // Fetch course details for each course to get category
      const coursePromises = courses.map(async (course) => {
        try {
          const courseData = await courseService.getCourse(course.courseId);
          if (courseData) {
            const category = courseData.category || 'Other';
            const existing = categoryMap.get(category) || { total: 0, completed: 0 };
            
            existing.total += 1;
            if (course.status === 'completed' || course.progress >= 100) {
              existing.completed += 1;
            }
            
            categoryMap.set(category, existing);
          }
        } catch (e) {
          // Ignore errors for individual courses
        }
      });

      await Promise.all(coursePromises);

      // Calculate average completion across categories
      if (categoryMap.size > 0) {
        let totalPercentage = 0;
        categoryMap.forEach((data) => {
          totalPercentage += (data.completed / data.total) * 100;
        });
        const avgQuotient = Math.round(totalPercentage / categoryMap.size);
        setAiQuotient(avgQuotient);
      } else {
        setAiQuotient(0);
      }
    } catch (error) {
      console.error("Error calculating AI Quotient:", error);
      setAiQuotient(0);
    }
  };

    fetchJourney();
  }, [user, user?.currentLearningPlanId]); // Re-fetch when currentLearningPlanId changes

  // Function to scroll to a section
  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const headerOffset = 100; // Adjust for fixed header
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
  
      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth"
      });
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!journeyData) {
    return (
      <div className="max-w-4xl mx-auto py-8 px-4">
        <div className="text-center py-12">
          <BookOpen className="w-12 h-12 text-neutral-300 mx-auto mb-4" />
          <p className="text-neutral-500">No learning journey data found.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto py-8 px-4">
      
      {/* Summary Stats - Order: In Progress, Current Plan, Previous Plans, Completed, AI Quotient */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-12">
        {/* 1. In Progress */}
        <div 
          onClick={() => (journeyData.inProgressCourses.length > 0) && scrollToSection('journey-section-in-progress')}
          className={`bg-white rounded-xl p-4 shadow-sm border border-neutral-100 flex flex-col items-center justify-center text-center transition-all ${(journeyData.inProgressCourses.length > 0) ? 'cursor-pointer hover:shadow-md hover:border-blue-200' : 'opacity-70'}`}
        >
          <div className="text-3xl font-bold text-blue-600 mb-1">{journeyData.inProgressCourses.length}</div>
          <div className="text-xs font-semibold text-neutral-500 uppercase tracking-wide">In Progress</div>
        </div>

        {/* 2. Current Plan */}
        <div 
          onClick={() => (journeyData.currentPlanToStartCourses.length > 0) && scrollToSection('journey-section-current-plan')}
          className={`bg-white rounded-xl p-4 shadow-sm border border-neutral-100 flex flex-col items-center justify-center text-center transition-all ${(journeyData.currentPlanToStartCourses.length > 0) ? 'cursor-pointer hover:shadow-md hover:border-amber-200' : 'opacity-70'}`}
        >
          <div className="text-3xl font-bold text-amber-600 mb-1">{journeyData.currentPlanToStartCourses.length}</div>
          <div className="text-xs font-semibold text-neutral-500 uppercase tracking-wide">Current Plan</div>
        </div>

        {/* 3. Previous Plans */}
        <div 
          onClick={() => (journeyData.previousPlansToStartCourses.length > 0) && scrollToSection('journey-section-previous-plans')}
          className={`bg-white rounded-xl p-4 shadow-sm border border-neutral-100 flex flex-col items-center justify-center text-center transition-all ${(journeyData.previousPlansToStartCourses.length > 0) ? 'cursor-pointer hover:shadow-md hover:border-neutral-200' : 'opacity-70'}`}
        >
          <div className="text-3xl font-bold text-neutral-600 mb-1">{journeyData.previousPlansToStartCourses.length}</div>
          <div className="text-xs font-semibold text-neutral-500 uppercase tracking-wide">Previous Plans</div>
        </div>

        {/* 4. Completed */}
        <div 
          onClick={() => (journeyData.completedCourses.length > 0) && scrollToSection('journey-section-completed')}
          className={`bg-white rounded-xl p-4 shadow-sm border border-neutral-100 flex flex-col items-center justify-center text-center transition-all ${(journeyData.completedCourses.length > 0) ? 'cursor-pointer hover:shadow-md hover:border-green-200' : 'opacity-70'}`}
        >
          <div className="text-3xl font-bold text-green-600 mb-1">{journeyData.completedCourses.length}</div>
          <div className="text-xs font-semibold text-neutral-500 uppercase tracking-wide">Completed</div>
        </div>

        {/* 5. AI Quotient */}
        <div 
          onClick={() => scrollToSection('journey-section-categories')}
          className="bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl p-4 shadow-md border-2 border-purple-200 flex flex-col items-center justify-center text-center transition-all cursor-pointer hover:shadow-lg hover:scale-105 col-span-2 md:col-span-1"
        >
          <div className="text-3xl font-bold text-white mb-1">{aiQuotient}%</div>
          <div className="text-xs font-semibold text-white uppercase tracking-wide">AI Quotient</div>
          <div className="text-[10px] text-purple-100 mt-1">Avg. Coverage</div>
        </div>
      </div>

      <div className="relative">
        {/* Continuous Timeline Rail */}
        <div className="absolute left-6 top-4 bottom-4 w-0.5 bg-neutral-200"></div>

        <div className="space-y-12">
          
          {/* Section 1: In Progress */}
          {journeyData.inProgressCourses.length > 0 && (
            <div id="journey-section-in-progress" className="relative pl-20 animate-in fade-in slide-in-from-bottom-4 duration-500">
              <div className="absolute left-0 w-12 h-12 bg-blue-100 rounded-full border-4 border-white shadow-sm flex items-center justify-center z-10">
                <Rocket className="w-5 h-5 text-blue-600" />
              </div>
              
              <div className="mb-6">
                <h4 className="text-xl font-bold text-neutral-800">In Progress</h4>
                <p className="text-neutral-500 text-sm">Keep up the momentum.</p>
              </div>
              
              <div className="grid gap-4">
                {journeyData.inProgressCourses.map(course => (
                  <JourneyCourseCard 
                    key={course.courseId} 
                    course={course}
                    dateLabel={course.lastAccessedAt ? `Last active ${course.lastAccessedAt.toDate().toLocaleDateString()}` : 'In progress'}
                    variant="in-progress"
                  />
                ))}
              </div>
            </div>
          )}

          {/* Section 2: Current Learning Plan - To Start */}
          {journeyData.currentPlanToStartCourses.length > 0 && (
            <div id="journey-section-current-plan" className="relative pl-20 animate-in fade-in slide-in-from-bottom-4 duration-500 delay-100">
              <div className="absolute left-0 w-12 h-12 bg-amber-100 rounded-full border-4 border-white shadow-sm flex items-center justify-center z-10">
                <MapPin className="w-5 h-5 text-amber-600" />
              </div>
              
              <div className="mb-6">
                <h4 className="text-xl font-bold text-neutral-800">Current Learning Plan - To Start</h4>
                <p className="text-neutral-500 text-sm">To start from your latest plan.</p>
              </div>
              
              <div className="grid gap-4">
                {journeyData.currentPlanToStartCourses.map(course => (
                  <JourneyCourseCard 
                    key={course.courseId} 
                    course={course}
                    dateLabel={`Added ${course.addedAt.toDate().toLocaleDateString()}`}
                    variant="current-plan"
                  />
                ))}
              </div>
            </div>
          )}

          {/* Section 3: Previous Learning Plans - To Start */}
          {journeyData.previousPlansToStartCourses.length > 0 && (
            <div id="journey-section-previous-plans" className="relative pl-20 animate-in fade-in slide-in-from-bottom-4 duration-500 delay-200">
              <div className="absolute left-0 w-12 h-12 bg-neutral-100 rounded-full border-4 border-white shadow-sm flex items-center justify-center z-10">
                <History className="w-5 h-5 text-neutral-500" />
              </div>
              
              <div className="mb-6">
                <h4 className="text-xl font-bold text-neutral-800">Previous Learning Plans - To Start</h4>
                <p className="text-neutral-500 text-sm">Courses from earlier plans.</p>
              </div>
              
              <div className="grid gap-4">
                {journeyData.previousPlansToStartCourses.map(course => (
                  <JourneyCourseCard 
                    key={course.courseId} 
                    course={course}
                    dateLabel={`Added ${course.addedAt.toDate().toLocaleDateString()}`}
                    variant="default"
                  />
                ))}
              </div>
            </div>
          )}

          {/* Section 4: Completed */}
          {journeyData.completedCourses.length > 0 && (
            <div id="journey-section-completed" className="relative pl-20 animate-in fade-in slide-in-from-bottom-4 duration-500 delay-300">
              <div className="absolute left-0 w-12 h-12 bg-green-100 rounded-full border-4 border-white shadow-sm flex items-center justify-center z-10">
                <Trophy className="w-5 h-5 text-green-600" />
              </div>
              
              <div className="mb-6">
                <h4 className="text-xl font-bold text-neutral-800">Completed</h4>
                <p className="text-neutral-500 text-sm">Milestones achieved.</p>
              </div>
              
              <div className="grid gap-4">
                {journeyData.completedCourses.map(course => (
                  <JourneyCourseCard 
                    key={course.courseId} 
                    course={course} 
                    dateLabel={course.completedAt ? `Completed ${course.completedAt.toDate().toLocaleDateString()}` : 'Completed'}
                    variant="completed"
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Category Coverage Section */}
      <div id="journey-section-categories">
        <CategoryCoverage journeyData={journeyData} />
      </div>
    </div>
  );
};

// Category icons and colors mapping
const CATEGORY_CONFIG: Record<string, { icon: string; gradient: string; color: string }> = {
  'Fundamentals': { icon: '🎓', gradient: 'from-blue-500 to-cyan-400', color: 'blue' },
  'Programming': { icon: '💻', gradient: 'from-purple-500 to-pink-400', color: 'purple' },
  'Machine Learning': { icon: '🤖', gradient: 'from-green-500 to-teal-400', color: 'green' },
  'Deep Learning': { icon: '🧠', gradient: 'from-indigo-500 to-purple-400', color: 'indigo' },
  'Natural Language Processing': { icon: '💬', gradient: 'from-amber-500 to-orange-400', color: 'amber' },
  'Computer Vision': { icon: '👁️', gradient: 'from-rose-500 to-red-400', color: 'rose' },
  'Data Science': { icon: '📊', gradient: 'from-cyan-500 to-blue-400', color: 'cyan' },
  'Generative AI': { icon: '✨', gradient: 'from-violet-500 to-fuchsia-400', color: 'violet' },
  'Ethics and Governance': { icon: '⚖️', gradient: 'from-slate-500 to-gray-400', color: 'slate' },
  'AI Tools': { icon: '🛠️', gradient: 'from-emerald-500 to-green-400', color: 'emerald' },
  'Deployment': { icon: '🚀', gradient: 'from-sky-500 to-blue-400', color: 'sky' },
};

// Type for category data aggregation
type CategoryData = { total: number; completed: number; inProgress: number };

interface CategoryCoverageProps {
  journeyData: JourneyData;
}

const CategoryCoverage: React.FC<CategoryCoverageProps> = ({ journeyData }) => {
  // Aggregate all courses
  const allCourses = [
    ...journeyData.completedCourses,
    ...journeyData.inProgressCourses,
    ...journeyData.currentPlanToStartCourses,
    ...journeyData.previousPlansToStartCourses,
  ];

  // Fetch course details to get categories
  type CategoryMap = Map<string, CategoryData>;
  const [categoriesData, setCategoriesData] = React.useState<CategoryMap>(new Map());
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const fetchCategoryCoverage = async () => {
      try {
        setLoading(true);
        const categoryMap: CategoryMap = new Map();

        // Fetch course details for each course to get category
        const coursePromises = allCourses.map(async (course) => {
          try {
            const courseData = await courseService.getCourse(course.courseId);
            if (courseData) {
              const category = courseData.category || 'Other';
              const existing: CategoryData = categoryMap.get(category) || { total: 0, completed: 0, inProgress: 0 };
              
              existing.total += 1;
              if (course.status === 'completed' || (course.progress >= 100)) {
                existing.completed += 1;
              } else if (course.startedAt || (course.progress > 0)) {
                existing.inProgress += 1;
              }
              
              categoryMap.set(category, existing);
            }
          } catch (e) {
            console.error(`Error fetching course ${course.courseId}:`, e);
          }
        });

        await Promise.all(coursePromises);
        setCategoriesData(categoryMap);
      } catch (error) {
        console.error("Error calculating category coverage:", error);
      } finally {
        setLoading(false);
      }
    };

    if ((allCourses.length > 0)) {
      fetchCategoryCoverage();
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allCourses.length]);

  if (loading) {
    return (
      <div className="mt-16 text-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-primary mx-auto" />
      </div>
    );
  }

  if (categoriesData.size === 0) {
    return null;
  }

  // Sort categories by completion percentage (descending)
  const sortedCategories = Array.from(categoriesData.entries())
    .sort((a, b) => {
      const percentA = (a[1].completed / a[1].total) * 100;
      const percentB = (b[1].completed / b[1].total) * 100;
      return percentB - percentA;
    });

  return (
    <div className="mt-16 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div className="mb-8">
        <h3 className="text-2xl font-bold text-neutral-800 mb-2">Your AI Knowledge Coverage</h3>
        <p className="text-neutral-600">
          Track your progress across different AI domains and see where you're building expertise.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {sortedCategories.map(([category, data]: [string, CategoryData]) => {
          const completionPercent = Math.round((data.completed / data.total) * 100);
          const config = CATEGORY_CONFIG[category] || { icon: '📚', gradient: 'from-neutral-500 to-gray-400', color: 'neutral' };
          
          return (
            <Card key={category} className="p-6 hover:shadow-lg transition-shadow duration-300">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${config.gradient} flex items-center justify-center text-2xl shadow-md`}>
                    {config.icon}
                  </div>
                  <div>
                    <h4 className="font-semibold text-lg text-neutral-800">{category}</h4>
                    <p className="text-sm text-neutral-500">
                      {data.completed} of {data.total} courses completed
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <div className={`text-2xl font-bold text-${config.color}-600`}>
                    {completionPercent}%
                  </div>
                </div>
              </div>

              {/* Progress bar */}
              <div className="relative">
                <div className="w-full h-3 bg-neutral-100 rounded-full overflow-hidden">
                  <div
                    className={`h-full bg-gradient-to-r ${config.gradient} rounded-full transition-all duration-500 ease-out`}
                    style={{ width: `${completionPercent}%` }}
                  ></div>
                </div>
                
                {/* Course status indicators */}
                <div className="flex items-center gap-4 mt-3 text-xs text-neutral-600">
                  {(data.completed > 0) && (
                    <div className="flex items-center gap-1">
                      <div className="w-3 h-3 rounded-full bg-green-500"></div>
                      <span>{data.completed} completed</span>
                    </div>
                  )}
                  {(data.inProgress > 0) && (
                    <div className="flex items-center gap-1">
                      <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                      <span>{data.inProgress} in progress</span>
                    </div>
                  )}
                  {((data.total - data.completed - data.inProgress) > 0) && (
                    <div className="flex items-center gap-1">
                      <div className="w-3 h-3 rounded-full bg-neutral-300"></div>
                      <span>{data.total - data.completed - data.inProgress} to start</span>
                    </div>
                  )}
                </div>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

interface JourneyCourseCardProps {
  course: ExtendedCourse;
  dateLabel: string;
  variant: 'completed' | 'in-progress' | 'current-plan' | 'default';
}

const JourneyCourseCard: React.FC<JourneyCourseCardProps> = ({ course, dateLabel, variant }) => {
  const navigate = useNavigate();
  const isCompleted = variant === 'completed';
  const isInProgress = variant === 'in-progress';
  const isCurrentPlan = variant === 'current-plan';

  // Dynamic styles based on variant
  let cardStyles = "p-5 transition-all duration-300 hover:shadow-lg border-l-4";
  let progressBarColor = "bg-primary";
  let iconBg = "bg-primary-100";
  let iconColor = "text-primary-600";
  
  if (isCompleted) {
    cardStyles += " border-l-green-500 bg-white/80 hover:bg-white";
    progressBarColor = "bg-green-500";
    iconBg = "bg-green-100";
    iconColor = "text-green-600";
  } else if (isInProgress) {
    cardStyles += " border-l-blue-500 shadow-md transform hover:-translate-y-1";
    progressBarColor = "bg-gradient-to-r from-blue-500 to-cyan-400";
    iconBg = "bg-blue-100";
    iconColor = "text-blue-600";
  } else if (isCurrentPlan) {
    cardStyles += " border-l-amber-400 shadow-sm hover:shadow-md";
    progressBarColor = "bg-amber-400";
    iconBg = "bg-amber-100";
    iconColor = "text-amber-600";
  } else {
    cardStyles += " border-l-neutral-300 opacity-90 hover:opacity-100";
    progressBarColor = "bg-neutral-400";
    iconBg = "bg-neutral-100";
    iconColor = "text-neutral-500";
  }

  return (
    <Card className={cardStyles}>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-start gap-4 flex-1 min-w-0">
          {/* Status Icon */}
          <div className={`mt-1 rounded-full p-2 flex-shrink-0 ${iconBg}`}>
            {isCompleted ? (
              <CheckCircle className={`w-5 h-5 ${iconColor}`} />
            ) : isInProgress ? (
              <Rocket className={`w-5 h-5 ${iconColor}`} />
            ) : (
              <Sparkles className={`w-5 h-5 ${iconColor}`} />
            )}
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 mb-1">
              {isInProgress && (
                <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-[10px] font-bold uppercase tracking-wider rounded-full">
                  Active
                </span>
              )}
              <span className="text-xs text-neutral-400 font-medium">{dateLabel}</span>
            </div>
            
            <h4 className="font-bold text-lg text-neutral-800 break-words leading-tight mb-3">
              {course.courseTitle}
            </h4>
            
            <div className="flex items-center gap-3">
               <div className="w-32 h-2 bg-neutral-100 rounded-full overflow-hidden flex-shrink-0">
                  <div 
                    className={`h-full rounded-full transition-all duration-500 ${progressBarColor}`} 
                    style={{ width: `${course.progress}%` }}
                  ></div>
               </div>
               <span className="text-sm font-semibold text-neutral-600 flex-shrink-0">
                 {Math.round(course.progress)}%
               </span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 self-end sm:self-center flex-shrink-0">
          {isCompleted ? (
            <Button 
               variant="outline" 
               icon={Award}
               className="hover:bg-green-50 hover:text-green-700 hover:border-green-200"
               onClick={() => navigate(`/certificate/${course.courseId}`)}
            >
              Certificate
            </Button>
          ) : (
            <Button 
               variant={isInProgress ? "primary" : "outline"}
               icon={Play}
               className={isInProgress ? "shadow-lg shadow-blue-200" : ""}
               onClick={() => navigate(`/courses/${course.courseId}`)}
            >
              {isInProgress ? 'Resume' : 'Start'}
            </Button>
          )}
        </div>
      </div>
    </Card>
  );
};